import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ReportsService } from 'src/app/services/reports.service';

// Plain, report-specific filter payload for this popup - deliberately NOT
// LotteryDrillDownContext (the dashboard/division/subDivision/village/beneficiary-list
// navigation chain's own shape, which also carries a real lotteryRunId anchored to a
// clicked row). This screen has no such row - only the filter form on
// lottery-report.component.ts - so it queries the report API directly with plain
// scalar values instead of borrowing that drill-down concept.
//
// departmentName/productName are display-only strings (resolved from the parent
// form's already-loaded lookup lists) - they thread through to ReportSearchViewModel's
// DepartmentName/ProductName for the RDLC page header (Month/Department/Product
// banner); they play no part in the actual report query.
export interface LotteryReportPopupData {
  financialYearId: number;
  departmentId: number;
  departmentName: string;
  goId: number;
  schemeId: number;
  componentId: number;
  productId: number;
  productName: string;
  divisionId: number;
  subDivisionId: number;
  villageId: number;
  lotteryDate: string;
}

@Component({
  selector: 'app-lottery-report-popup',
  templateUrl: './lottery-report-popup.component.html',
  styleUrls: ['./lottery-report-popup.component.scss']
})
export class LotteryReportPopupComponent implements OnInit {

  loading = false;
  reportHtml: SafeHtml | null = null;

  constructor(
    public dialogRef: MatDialogRef<LotteryReportPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LotteryReportPopupData,
    private reportsService: ReportsService,
    private sanitizer: DomSanitizer
  ) { }

  // On-screen view = the same RDLC-rendered Html this popup's own downloadhtml()
  // produces (downloadType 4) - just displayed inline via [innerHTML] instead of
  // triggering a download. Same pattern showdistrictwisestatusreport.component.ts's
  // own ngOnInit uses for its inline preview.
  ngOnInit(): void {
    this.loading = true;
    this.reportsService.lotteryreport(this.reportRequest(4)).subscribe((result: string) => {
      this.reportHtml = result ? this.sanitizer.bypassSecurityTrustHtml(atob(result)) : null;
      this.loading = false;
    }, () => {
      this.reportHtml = null;
      this.loading = false;
    });
  }

  private get fileNameBase(): string {
    return 'Lottery Report' + (this.data?.lotteryDate ? ' - ' + this.data.lotteryDate : '');
  }

  // ------ Backed by ReportType.LotteryReport (66) -> ReportController.LoadReportByType ->
  // Rdlc/LotteryReport.rdlc, via ReportsService.lotteryreport() - a thin, clearly-named
  // wrapper around the generic tripreport() (POST /api/report) every other RDLC-backed
  // report screen in this app uses.

  // Maps this popup's own filter fields (LotteryReportPopupData) onto
  // ReportSearchViewModel's field names. DepartmentName/ProductName are
  // display-only strings for the report's page header (Month/Department/Product
  // banner) - resolved by the parent (lottery-report.component.ts) from its own
  // already-loaded lookup lists, not from anything server-side.
  private reportRequest(downloadType: number): any {
    return {
      financialyearId: this.data.financialYearId,
      departmentId: this.data.departmentId,
      DepartmentName: this.data.departmentName || '',
      GoId: this.data.goId,
      schemeId: this.data.schemeId,
      componentId: this.data.componentId,
      ProductId: this.data.productId,
      ProductName: this.data.productName || 'All Products',
      DivisionId: this.data.divisionId,
      SubDivisionId: this.data.subDivisionId,
      VillageId: this.data.villageId,
      StartTs: this.data.lotteryDate,
      EndTs: this.data.lotteryDate,
      ReportType: 66,
      downloadType
    };
  }

  // downloadpdf(): void {
  //   this.reportsService.lotteryreport(this.reportRequest(1)).subscribe((result: string) => {
  //     if (!result) {
  //       return;
  //     }
  //     const downloadLink = document.createElement('a');
  //     downloadLink.href = 'data:application/pdf;base64, ' + result;
  //     downloadLink.download = this.fileNameBase + '.pdf';
  //     downloadLink.click();
  //   });
  // }

  downloadexcel(): void {
    this.reportsService.lotteryreport(this.reportRequest(2)).subscribe((result: string) => {
      if (!result) {
        return;
      }
      const downloadLink = document.createElement('a');
      downloadLink.href = 'data:application/vnd.ms-excel;base64, ' + result;
      downloadLink.download = this.fileNameBase + '.xls';
      downloadLink.click();
    });
  }

  // downloadhtml(): void {
  //   this.reportsService.lotteryreport(this.reportRequest(4)).subscribe((result: string) => {
  //     if (!result) {
  //       return;
  //     }
  //     const downloadLink = document.createElement('a');
  //     downloadLink.href = 'data:text/html;base64, ' + result;
  //     downloadLink.download = this.fileNameBase + '.html';
  //     downloadLink.click();
  //   });
  // }

  // downloadword(): void {
  //   this.reportsService.lotteryreport(this.reportRequest(3)).subscribe((result: string) => {
  //     if (!result) {
  //       return;
  //     }
  //     const downloadLink = document.createElement('a');
  //     downloadLink.href = 'data:application/msword;base64, ' + result;
  //     downloadLink.download = this.fileNameBase + '.doc';
  //     downloadLink.click();
  //   });
  // }

  // print(): void {
  //   this.reportsService.lotteryreport(this.reportRequest(1)).subscribe((result: string) => {
  //     if (!result) {
  //       return;
  //     }
  //     this.printPreview(result);
  //   });
  // }

  // printPreview = (data: string, type = 'application/pdf') => {
  //   const blob = this.b64toBlob(data, type);
  //   const blobURL = URL.createObjectURL(blob);
  //   const theWindow = window.open(blobURL);
  //   const theDoc = theWindow.document;
  //   const theScript = document.createElement('script');
  //   function injectThis() {
  //     window.print();
  //   }
  //   theScript.innerHTML = `window.onload = ${injectThis.toString()};`;
  //   theDoc.body.appendChild(theScript);
  // };

  // b64toBlob = (content: string, contentType: string) => {
  //   contentType = contentType || '';
  //   const sliceSize = 512;
  //   const byteCharacters = window.atob(content);

  //   const byteArrays = [];
  //   for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
  //     const slice = byteCharacters.slice(offset, offset + sliceSize);
  //     const byteNumbers = new Array(slice.length);
  //     for (let i = 0; i < slice.length; i++) {
  //       byteNumbers[i] = slice.charCodeAt(i);
  //     }
  //     const byteArray = new Uint8Array(byteNumbers);
  //     byteArrays.push(byteArray);
  //   }
  //   return new Blob(byteArrays, { type: contentType });
  // };

  onCancel(): void {
    this.dialogRef.close();
  }
}
